import React from 'react';
import { hooks } from '@lifesize/clients.sdk';
import { connect } from 'react-redux';
import { closeFullScreenModal } from 'actions/FullScreenModal';
import { SCHEDULE_MODAL } from 'constants/ScheduleModal';
import { START_CALL_MODAL } from 'constants/StartCallModal';
import { CONTACT_MODAL } from 'constants/ContactModal';
import { SETTINGS_MODAL } from 'constants/SettingsModal';
import { JOIN_MODAL } from 'constants/JoinModal';
import { internalModals } from 'constants/FullScreenModal';
import JoinModal from 'containers/JoinModal/JoinModalContainer';
import FullScreenModal from 'components/FullScreenModal/FullScreenModal';
import RetryComp from 'components/Retry/Retry';
import ScheduleModal from 'containers/ScheduleModal/ScheduleModalContainer';
import StartCallModal from 'containers/StartCallModal/StartCallModalContainer';
import StartingCallModal from 'containers/StartingCallModal/StartingCallModalContainer';
import DroppedCallModal from 'containers/DroppedCallModal/DroppedCallModalContainer';
import CreateContactModal from 'containers/ContactModal/ContactModalContainer';
import SettingsModal from 'containers/SettingsModal/SettingsModalContainer';
import { navigateToTrialPage } from 'actions/Application';
import { callActions, networkSelectors, servicesSelectors, userProfileSelectors } from '@lifesize/nucleus';
import { isStartingCall, callDisconnectedWithReason, isDroppedCall } from 'utils/call-utils';
import _get from 'lodash/get';
import { selectIsLoggedIn } from 'selectors/auth';
import { stopWebcam } from 'actions/media';
import { DELETE_CHAT_MODAL } from 'constants/Chat';
import DeleteChatModal from 'containers/DeleteChatModal/DeleteChatModalContainer';

const mapDispatchToProps = (dispatch) => ({
  onCloseClicked: (component, callId, isGuest) => {
    // If we are currently displaying a starting call modal, we need to treat
    // the FSM cancel 'X' button as an end call event.
    if (component === internalModals.STARTING_CALL_MODAL || component === internalModals.DROPPED_CALL_MODAL) {
      dispatch(callActions.cancel());
      dispatch(callActions.clearCall());
      if (isGuest) {
        dispatch(navigateToTrialPage());
      }
    }
    if (component === internalModals.JOIN_MODAL) {
      dispatch(stopWebcam());
    }
    dispatch(closeFullScreenModal());
  }
});

const visibleComponent = (state, ownProps) => {
  const { activeCall } = ownProps;

  // If offline, display offline modal
  const authFailure = servicesSelectors.selectConnected(state);
  const hasAttemptedLogin = _get(state, 'app.jwt.hasAttemptedLogin');
  const isLoggedIn = selectIsLoggedIn(state);

  if (isDroppedCall(activeCall)) {
    return internalModals.DROPPED_CALL_MODAL;
  }

  if (servicesSelectors.selectCanConnect(state) && !authFailure && hasAttemptedLogin) {
    return internalModals.RELOAD_MODAL;
  }

  if (networkSelectors.isOffline(state) && (hasAttemptedLogin || isLoggedIn)) {
    return internalModals.OFFLINE_MODAL;
  }

  // If the app has an active call, and it is not connected yet, display the
  // starting call modal. Otherwise, if there is a selected FSM subcomponent,
  // display that.
  // GAC NOTE: The eventual goal is to have all modals be displayed reactively by
  // observing states. Not sure how we get there, but it is the ideal.
  return isStartingCall(activeCall) || callDisconnectedWithReason(activeCall)
    ? internalModals.STARTING_CALL_MODAL
    : _get(state, 'app.modal.visibleComponent');
};

const getSubComponent = (selectedComponent, props) => {
  switch (selectedComponent) {
    case internalModals.RELOAD_MODAL:
      return (
        <div style={{ height: '100%' }}>
          <RetryComp />
        </div>
      );
    case internalModals.OFFLINE_MODAL:
      return (
        <div style={{ height: '100%' }}>
          <RetryComp noRetry />
        </div>
      );
    case internalModals.DROPPED_CALL_MODAL:
      return <DroppedCallModal {...props} />;
    case START_CALL_MODAL:
      return <StartCallModal {...props} />;
    case internalModals.STARTING_CALL_MODAL:
      return <StartingCallModal {...props} />;
    case SCHEDULE_MODAL:
      return <ScheduleModal {...props} />;
    case CONTACT_MODAL:
      return <CreateContactModal {...props} />;
    case SETTINGS_MODAL:
      return <SettingsModal {...props} />;
    case JOIN_MODAL:
      return <JoinModal {...props} />;
    case DELETE_CHAT_MODAL:
      return <DeleteChatModal {...props} />;
    default:
      return <div />;
  }
};

const message = (state) => _get(state, 'app.modal.message');

const mapStateToProps = (state, ownProps) => {
  const { activeCall } = ownProps;
  const isGuest = userProfileSelectors.selectIsGuest(state);
  const visibleComponentValue = visibleComponent(state, ownProps);
  const childrenValue = getSubComponent(visibleComponentValue, _get(state, 'app.modal.modalProps'));

  const nonDismissable = visibleComponentValue === internalModals.STARTING_CALL_MODAL;
  const priority = visibleComponentValue === internalModals.RELOAD_MODAL ? 200 : 199;

  return {
    activeCall,
    children: childrenValue,
    disableCloseButton: isGuest && visibleComponentValue === JOIN_MODAL,
    internalModals,
    isGuest,
    message: message(state),
    nonDismissable,
    priority,
    visibleComponent: visibleComponentValue
  };
};

const mergeProps = (stateProps, dispatchProps, ownProps) =>
  Object.assign({}, ownProps, stateProps, dispatchProps, {
    onCloseClicked: (component) => dispatchProps.onCloseClicked(component, stateProps.activeCall.id, stateProps.isGuest)
  });

const ConnectedComponent = connect(mapStateToProps, mapDispatchToProps, mergeProps)(FullScreenModal);

const FunctionalWrapper = (props) => {
  const call = hooks.useCallState();
  const conference = hooks.useConferenceInfo();
  return <ConnectedComponent {...props} activeCall={call} conference={conference} />;
};
export { ConnectedComponent as FullScreenModalContainer };
export default FunctionalWrapper;
